import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Optional, Self, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { LookupResponse } from '@core/models/common.model';
import { SharedMaterialUIModule } from '../../modules/shared-material-ui.module';

// A dropdown over one reference list, with an optional "None" choice. It is the plain sibling of searchable-lookup-select (which is for
// long lists): use it as `<lookup-select formControlName="x" label="..." [options]="..." />`. The control's value is the chosen item's id,
// or '' for None.
//
// The select inside is bound to the same control as the one outside, so Material does the work it does for any field bound to a control:
// the value, disabled, the required marker, and the red error state. This component only draws the field. It is therefore not a value
// accessor itself (the methods below do nothing), but it must say so, or the form would look for one.
@Component({
  selector: 'lookup-select',
  imports: [ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './lookup-select.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LookupSelectComponent implements ControlValueAccessor, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input.required<string>();
  readonly options = input<LookupResponse[]>([]);
  // The empty choice at the top of the list; its text can be changed ("Other eligible family member").
  readonly allowNone = input(true);
  readonly noneLabel = input('None');
  readonly hint = input<string | null>(null);
  // An info icon after the field, with this text as its tooltip.
  readonly tooltip = input<string | null>(null);
  readonly requiredError = input('This field is required.');

  // The id that was chosen, for a caller that does more than store it (clearing a dependent field, for example).
  readonly selectionChanged = output<string>();

  // Bumped whenever the control is touched or changes status. A form marked as touched by code (a step that is not complete) would not
  // otherwise redraw this view, and the select would not work out its error state.
  private readonly controlState = signal(0);

  private readonly fallbackControl = new FormControl('');

  constructor(@Optional() @Self() protected readonly ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // The control the field is bound to (a free-standing one when the component is used without a form).
  protected get control(): FormControl {
    return (this.ngControl?.control as FormControl | null | undefined) ?? this.fallbackControl;
  }

  ngAfterViewInit(): void {
    this.ngControl?.control?.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.controlState.update((count) => count + 1));
  }

  // Value accessor members: nothing to do, the select inside is bound to the control directly.
  writeValue(): void {}
  registerOnChange(): void {}
  registerOnTouched(): void {}

  protected hasRequiredError(): boolean {
    this.controlState();
    return this.control.touched && this.control.hasError('required');
  }

  protected optionLabel(item: LookupResponse): string {
    return item.name ?? item.code ?? '';
  }
}
